import type { HTMLAttributes } from "react";
import { cn } from "./cn";
import { Badge as UiBadge } from "./ui/badge";

export type BadgeVariant = "neutral" | "success" | "warning" | "danger" | "info";

const indicatorColors: Record<BadgeVariant, string> = {
  neutral: "bg-zinc-400 dark:bg-zinc-500",
  success: "bg-emerald-600 dark:bg-emerald-400",
  warning: "bg-amber-500 dark:bg-amber-400",
  danger: "bg-red-600 dark:bg-red-400",
  info: "bg-blue-600 dark:bg-blue-400"
};
const labelColors: Record<BadgeVariant, string> = {
  neutral: "text-muted-foreground",
  success: "text-emerald-700 dark:text-emerald-400",
  warning: "text-amber-800 dark:text-amber-400",
  danger: "text-red-700 dark:text-red-400",
  info: "text-blue-700 dark:text-blue-400"
};
export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  dot?: boolean;
}

export function Badge({ variant = "neutral", dot = false, className, children, ...props }: BadgeProps) {
  if (dot) {
    return (
      <span className={cn("inline-flex max-w-full items-center gap-2 whitespace-nowrap text-xs font-normal text-foreground", className)} {...props}>
        <span className={cn("size-1.5 shrink-0 rounded-full", indicatorColors[variant])} aria-hidden="true" />
        <span className="truncate">{children}</span>
      </span>
    );
  }
  return (
    <UiBadge variant="secondary" className={cn("max-w-full rounded-sm border-0 bg-muted px-1.5 py-0.5 text-[11px] font-normal", labelColors[variant], className)} {...props}>
      <span className="truncate">{children}</span>
    </UiBadge>
  );
}

const statuses: Record<string, { variant: BadgeVariant; label: string }> = {
  ACTIVE: { variant: "success", label: "Ativo" },
  OWNER: { variant: "info", label: "Líder" },
  MEMBER: { variant: "neutral", label: "Membro" },
  BANNED: { variant: "danger", label: "Banido" },
  DISABLED: { variant: "neutral", label: "Desativado" },
  DISBANDED: { variant: "neutral", label: "Encerrado" },
  REMOVED: { variant: "neutral", label: "Removido" },
  REVOKED: { variant: "neutral", label: "Revogado" },
  EXPIRED: { variant: "neutral", label: "Expirado" },
  PENDING: { variant: "warning", label: "Pendente" },
  SOLD: { variant: "neutral", label: "Vendido" },
  COMPLETED: { variant: "success", label: "Concluído" },
  CANCELLED: { variant: "neutral", label: "Cancelado" }
};

export function StatusBadge({ status, className }: { status: string; className?: string }) {
  const display = statuses[status.toUpperCase()];
  return (
    <Badge variant={display?.variant ?? "neutral"} dot className={className} title={status}>
      {display?.label ?? status}
    </Badge>
  );
}
